import { createApi } from '@reduxjs/toolkit/query/react'
import { API_ENDPOINTS } from 'Constants'
import { updateAssuredData, updateCustomerData } from 'features/BobSellJourney/CbsData.slice'
import { IPlanQuoteResponse } from 'Models/BOB/ResponseModels'
import { INestedQuestionSet } from 'Models/BOB/ResponseModels/Life/QuestionSet'
import {
  IQuoteData,
  IRecommendedPlan,
  IRecommendedQuotes,
} from 'Models/BOB/ResponseModels/Life/RecommendedQuotes'
import { INomineeRequest, ISubmitExistingPolicyRequest } from 'Models/RequestModels'
import { IEcsFormRequest } from 'Models/RequestModels/Life/EcsFormRequest'
import { ISpUser } from 'Models/ResponseModels/authentication'
import { ecsFormObject } from 'Pages/Life/BobLifeSteppers/OtherDetails/Forms/EcsForm/ecsRenderObject'
import { eiaFormObject } from 'Pages/Life/BobLifeSteppers/OtherDetails/Forms/EiaForm/eiaRenderObject'
import {
  fatcaFormObject,
  // fatcaFormObject2,
} from 'Pages/Life/BobLifeSteppers/OtherDetails/Forms/FatcaForm/fatcaRenderObject'
import { FieldValues } from 'react-hook-form'
import { bobProductUrlQuery } from 'Services/baseQuery'
import { number } from 'zod'

const hardCodedQuotePayload = {
  la_customer_id: 'e829f9c0-f5e6-48c8-b862-19d5d22a89d1',
  pr_customer_id: 'e829f9c0-f5e6-48c8-b862-19d5d22a89d1',
  partner_id: '1',
  product_category_code: 'life',
  relationship: 'self',
  suitability_data: {
    premium: '1000',
    term: '1',
    life_stage: 'Salaried',
    occupation: 'Judge',
    risk_appetite: '1',
    business_type: 'Farmer',
    policy_count: '0',
    type_of_insurance: 'Traditional',
    goals: { '1': 'wealth', '2': 'child_future', '3': 'retirement' },
  },
}
interface IPlanQuoteBody {
  la_customer_id: string
  pr_customer_id: string
  partner_id: string
  product_category_code: string
  plan_code: string
  plan_data: {
    plan_options: any
    selected_riders: any
  }
  agent_details: any
  quote_id: string
  lead_id: string
  is_recommended:boolean
  relationship?:string,
}

interface ISuitabilityData {
  time_horizon?: string
  current_life_stage?: string
  business_type?: string
  annual_disposable_income?: string
  current_policy?: string
  insurance_type?: string
  occupation?: string
  age_band?: string
  goals?: Record<string, number>
  goal_amounts?: Record<string, number>
  device?: string
}

interface IRecommendedBody {
  branch_id:string|number
  customer_id_value:string|number
  customer_id_type:string|number
  la_customer_id: string
  pr_customer_id: string
  partner_id: string
  product_category_code: string
  relationship: string
  suitability_data: ISuitabilityData
  lead_id: string
}
interface IConsentLinkParams {
  stage: string
  planquote_id?: string
  proposal_id?: string
  agent_id: string
  mobile_no?: string
}

export interface IDisabilityConsentSendLinkRequest {
  mobile: string
  email: string
  name: string
  customer_name: string
  proposal_id: string
  lead_id: string
  agent_id: string
  personal_details: Record<string, string | number | boolean | string[] | undefined>
  disability_data: Record<string, string | number | boolean | string[] | undefined>
}

export interface IDisabilityConsentSendLinkResponse {
  consent_id?: string
  link?: string
}

export interface IDisabilityConsentStatusResponse {
  consent_id?: string
  consent_status?: string
  is_approved?: boolean
  otp_sent?: boolean
  mobile?: string
  email?: string
  link_sent_at?: string
  updated_at?: string
}

interface IInitialConsentLinkParams {
  agent_id: string
  stage: string
  email_id: string
  customer_name: string
  mobile_no: string
  account_number: string
  lead_id: string
  product_category: string
}

export interface ISPData {
  'email-id': string
  employee_id: number
  mobile_no: number
  name: string
}
export interface ISalesReport {
  id: string
  insurance_vertical: string
  insurance_company: string
  product_category: string
  product_name: string
  proposer_name: string
  life_assured_name: string
  policy_login_date: string
  application_number: string
  policy_issue_date: string
  policy_number: string
  policy_status: string
  premium_amount_with_tax: number
  sum_assured: number
  lead_id: string
  policy_pdf: string
}
export interface IDocListResponse {
  pa?: Record<string, string[]>
  la?: Record<string, string[]>
  default?: {
    pa?: Record<string, Record<string, { type: 'fms_ref_id' | 'base64'; value: string }>>
    la?: Record<string, Record<string, { type: 'fms_ref_id' | 'base64'; value: string }>>
  }
}

export interface IPaymentResponse {
  proposal_payment_status: string
  retryable: boolean
  payment_data: IPaymentData
}

export interface IPaymentData {
  is_sucess: boolean
  application_number: string
  amount_paid: number
  insurance: string
  payment_date: string
  payment_mode: string
  transaction_id: string
}

export const bobProductApi = createApi({
  reducerPath: 'lifeApi',
  baseQuery: bobProductUrlQuery,
  endpoints: (builder) => ({
    getSuitabilityMatrixQuestionsList: builder.query<
      any,
      {
        question_set: string
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_QUESTIONS_LIST,
        method: 'GET',
        params: params,
      }),
    }),
    getQuotePlan: builder.query<any, any>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.QUOTE_FORM_PLAN,
        method: 'GET',
        params: params,
      }),
    }),

    getPlanOptionQuestionList: builder.query<
      { form_fields: INestedQuestionSet[] },
      {
        question_set: 'PlanOptions'
        plan: string
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_QUESTIONS_LIST,
        method: 'GET',
        params: params,
      }),
    }),
    getEKYCLifeAssuredCustomer: builder.query<any, any>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.BOB_EKYC_CUSTOMER,
        method: 'GET',
        params: params,
      }),
    }),
    getSPData: builder.query<ISPData[], any>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_SP_DATA,
        method: 'GET',
        params: params,
      }),
    }),
    getRecommendedPlans: builder.mutation<IRecommendedQuotes, IRecommendedBody>({
      query: (data) => ({
        url: API_ENDPOINTS.BOB.RECOMMENDED_PLANS,
        method: 'POST',
        body: data,
      }),
    }),
    // NO AUTH
    getRelationships: builder.query<any, any>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.BOB_RELATIONSHIPS,
        method: 'GET',
        params: params,
        headers: { 'Content-Type': 'application/json', Lang: 'EN' },
      }),
    }),
    getQuotations: builder.mutation<IPlanQuoteResponse, IPlanQuoteBody>({
      query: (data) => ({
        url: API_ENDPOINTS.BOB.GET_QUOTES,
        method: 'POST',
        body: data,
      }),
    }),

    getCreateProposalStatus: builder.mutation<
      {
        success: boolean
        proposal_data?: any
        proposal_form?: any
        proposal_id?: string
      },
      { planquote_id: string; lead_id: string }
    >({
      query: (data) => ({
        url: API_ENDPOINTS.BOB.GET_CREATE_PROPOSAL,
        method: 'POST',
        body: data,
      }),
    }),
    getChoices: builder.query<Record<string, string>, { choice_tag: string }>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.BOB_LIST_FORM,
        method: 'GET',
        params: params,
      }),
    }),

    getFatcaQuestions: builder.query<{ form_fields: INestedQuestionSet[] }, any>({
      queryFn() {
        const formData: { form_fields: INestedQuestionSet[] } = fatcaFormObject
        return {
          data: formData,
        }
      },
    }),
    getEcsQuestions: builder.query<{ form_fields: INestedQuestionSet[] }, any>({
      queryFn() {
        const formData: { form_fields: INestedQuestionSet[] } = ecsFormObject
        return {
          data: formData,
        }
      },
    }),
    getEiaQuestions: builder.query<{ form_fields: INestedQuestionSet[] }, any>({
      queryFn() {
        const formData: { form_fields: INestedQuestionSet[] } = eiaFormObject
        return {
          data: formData,
        }
      },
    }),
    getAssuredPersonalDetailsQuestions: builder.query<{ form_fields: INestedQuestionSet[] }, any>({
      queryFn() {
        const formData: { form_fields: INestedQuestionSet[] } = {
          form_fields: [
            {
              max: 10,
              name: 'Mobile number',
              code: 'contactnumber',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Mobile number',
              input_type: 'phone',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Email Address',
              code: 'emails',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Email Address',
              input_type: 'email',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Marital Status',
              code: 'marital_status',
              dropdown: { Single: 'Single', Married: 'Married' },
              cover_type: 'dropdown',
              min: undefined,
              description: 'Email Address',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: 6,
              name: 'Pin Code',
              code: 'pincode',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Pin Code',
              input_type: 'pincode',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'City',
              code: 'city',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'City',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'State',
              code: 'state',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'State',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Pan Card',
              code: 'pancard',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Pan Card',
              input_type: 'pancard',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Address',
              code: 'address',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Address',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Tabbacoo User',
              code: 'is_tobacco_user',
              dropdown: undefined,
              switch_options: { Yes: 'Yes', No: 'No' },
              cover_type: 'switch',
              min: undefined,
              description: 'Tabbacoo User',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Residential Status',
              code: 'residentialStatus',
              dropdown: { Indian: 'Indian', NRI: 'NRI', PIO: 'PIO' },
              cover_type: 'dropdown',
              min: undefined,
              description: 'Residential Status',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Nationality',
              code: 'nationality',
              dropdown: undefined,
              switch_options: { 'Non-Indian': 'Non-Indian', Indian: 'Indian' },
              cover_type: 'switch',
              min: undefined,
              description: 'Choose One of ',
              input_type: 'text',
              hidden: false,
              values: {},
            },
          ],
        }
        return {
          data: formData,
        }
      },
    }),

    getEducationalDetailsQuestions: builder.query<{ form_fields: INestedQuestionSet[] }, any>({
      queryFn() {
        const formData: { form_fields: INestedQuestionSet[] } = {
          form_fields: [
            {
              max: undefined,
              name: 'Occupation',
              code: 'occupation',
              dropdown: undefined,
              cover_type: 'dropdown',
              min: undefined,
              description: 'Occupation',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Educational Qualification',
              code: 'qualifications',
              dropdown: undefined,
              cover_type: 'dropdown',
              min: undefined,
              description: 'Educational Qualification',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Annual Income',
              code: 'annual_income',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Enter Annual Income',
              input_type: 'number',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Parent/Husband Insurance Details(Sum Insured)',
              code: 'parent_husband_insurance_details',
              dropdown: {
                '2 Lakhs': '2',
                '3 Lakhs': '3',
                '5 Lakhs': '5',
                '10 Lakhs': '10',
                '20 Lakhs': '20',
                '30 Lakhs': '30',
                '50 Lakhs': '50',
              },
              cover_type: 'dropdown',
              min: undefined,
              description: 'Parent/Husband Insurance Details(Sum Insured)',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Industry Type',
              code: 'industry_type',
              dropdown: {
                Jewellery: 'Jewellery',
                'Import/Export': 'Import/Export',
                Mining: 'Mining',
                Shipping: 'Shipping',
                'Scrap Dealing': 'Scrap Dealing',
                'Real Estate': 'Real Estate',
                Agriculture: 'Agriculture',
                'Stock Broking': 'Stock Broking',
                Others: 'Others',
              },
              cover_type: 'dropdown',
              min: undefined,
              description: 'Parent/Husband Insurance Details(Sum Insured)',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Is Existing Policy Holder?',
              code: 'isExistingPolicyHolder',
              dropdown: undefined,
              switch_options: { Yes: 'Yes', No: 'No' },
              cover_type: 'switch',
              min: undefined,
              description: 'Choose One of ',
              input_type: 'text',
              hidden: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Do you understand?',
              code: 'preferredLanguage',
              dropdown: undefined,
              switch_options: {
                English: 'English',
                Vernacular: 'Vernacular',
                Iliterate: 'Iliterate',
              },
              cover_type: 'radio',
              min: undefined,
              description: 'Choose One of ',
              input_type: 'text',
              hidden: false,
              values: {},
            },
          ],
        }
        return {
          data: formData,
        }
      },
    }),
    getExistingPolicyDetailsQuestions: builder.query<{ form_fields: INestedQuestionSet[] }, any>({
      queryFn() {
        const formData: { form_fields: INestedQuestionSet[] } = {
          form_fields: [
            {
              max: undefined,
              name: 'Policy Number',
              code: 'policyNumber',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Enter Policy Number',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Policy Type',
              code: 'policyType',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Enter Policy Type',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Annual Premium (in ₹)',
              code: 'annualPremium',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Enter Annual Premium',
              input_type: 'number',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Year of Commencement',
              code: 'commencementYear',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Enter Year of Commencement',
              input_type: 'number',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Sum Assured (Including Riders)',
              code: 'sumAssured',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Enter Sum Assured',
              input_type: 'number',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Policy Status',
              code: 'policyStatus',
              dropdown: {
                Inforced: 'Inforced',
                Lapsed: 'Lapsed',
                Applied: 'Applied',
              },
              cover_type: 'dropdown',
              min: undefined,
              description: 'Policy Status',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Policy Option',
              code: 'policyOption',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Enter Policy Option',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Term of Acceptance',
              code: 'termOfAcceptance',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Enter Term of Acceptance',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
            {
              max: undefined,
              name: 'Reason',
              code: 'details',
              dropdown: undefined,
              cover_type: 'Text',
              min: undefined,
              description: 'Enter Reason',
              input_type: 'text',
              hidden: false,
              range: false,
              values: {},
            },
          ],
        }
        return {
          data: formData,
        }
      },
    }),
    getEkycCustomer: builder.query<
      any,
      {
        [key: string]: string
        partner: string
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.BOB_EKYC_CUSTOMER,
        method: 'GET',
        params: params,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(updateAssuredData(data))
        } catch (e) {
          // console.log(e)
        }
      },
    }),

    submitExistingInsuranceDetals: builder.mutation<void, ISubmitExistingPolicyRequest>({
      query: (data) => ({
        url: API_ENDPOINTS.BOB.SUBMIT_EXISTING_INSURANCE_DETAILS,
        method: 'POST',
        body: data,
      }),
    }),

    updateDetails: builder.mutation<any, any>({
      query: (data) => ({
        url: API_ENDPOINTS.BOB.UPDATE_CUSTOMER_DETAILS,
        method: 'POST',
        body: data,
      }),
    }),

    submitNominee: builder.mutation<void, INomineeRequest>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.SUBMIT_NOMINEE,
        method: 'POST',
        body: body,
      }),
    }),
    submitFatcaDetails: builder.mutation<
      any,
      { proposal_id: string; customer_id: string; fatcaDetails: Record<string, any> }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.SUBMIT_FATCA_DETAILS,
        method: 'POST',
        body: body,
      }),
    }),
    submitEcsDetails: builder.mutation<void, IEcsFormRequest>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.SUBMIT_ECS_DETAILS,
        method: 'POST',
        body: body,
      }),
    }),
    submitEiaDetails: builder.mutation<
      void,
      {
        proposal_id: string
        lead_id: string
        eiaDetails: FieldValues
      }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.SUBMIT_EIA_DETAILS,
        method: 'POST',
        body: body,
      }),
    }),
    getDocList: builder.query<IDocListResponse, { proposal_id?: string }>({
      query: (proposal_id) => ({
        url: API_ENDPOINTS.BOB.GET_DOC_LIST,
        method: 'get',
        params: proposal_id,
      }),
    }),
    checkProposalStatus: builder.query<
      {
        proposal_status: string
        retryable: boolean
      },
      { proposal_id?: string }
    >({
      query: (proposal_id) => ({
        url: API_ENDPOINTS.BOB.CHECK_CONSENT_STATUS,
        method: 'get',
        params: proposal_id,
      }),
    }),
    getConsentFormLink: builder.query<string, IConsentLinkParams>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_CONSENT_FORM_LINK,
        method: 'GET',
        params: params,
      }),
    }),
    submitHealthDetails: builder.mutation<
      void,
      {
        proposal_id: string
        health_details: Record<string, string | boolean | number | string[]>
      }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.SUBMIT_HEALTH_DETAILS,
        method: 'POST',
        body: body,
      }),
    }),
    sendProposalOtp: builder.mutation<
      void,
      {
        proposal_id: string
      }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.SEND_PROPOSAL_OTP,
        method: 'POST',
        body: body,
      }),
    }),
    proposalSubmissionToInsurer: builder.mutation<
      any,
      {
        proposal_id: string
        is_partial_journey?: boolean
      }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.PROPOSAL_SUBMISSION_TO_INSURER,
        method: 'POST',
        body: body,
      }),
    }),
    getSendConsentLink: builder.query<
      void,
      {
        proposal_id: string
        stage: 'Proposal-Approval'
        agent_id: string
      }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_SEND_CONSENTLINK,
        method: 'GET',
        params: params,
      }),
    }),

    saveSutailblity: builder.mutation<
      {
        suitabilityId: string
      },
      {
        quote_data: IQuoteData
        recommended_plans: IRecommendedPlan[]
        proposedProduct: string
        quote_id: string
        advisorInformationDetails?: ISpUser
      }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.SAVE_SUITABILITY,
        method: 'POST',
        body: body,
      }),
    }),

    // Acr
    submitAcrDetails: builder.mutation<any, any>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.SUBMIT_ACR_DETAILS,
        method: 'POST',
        body: body,
      }),
    }),
    getCheckSpStatus: builder.query<any, { proposal_id?: string }>({
      query: (data) => ({
        url: API_ENDPOINTS.BOB.CHECK_SP_STATUS,
        method: 'GET',
        params: data,
      }),
    }),
    sendOtpSpConsent: builder.mutation<any, { proposal_id?: string }>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.SEND_OTP_SP_CONSENT,
        method: 'POST',
        body: body,
      }),
    }),
    verifyOtpSpConsent: builder.mutation<
      any,
      { proposal_id?: string; role: string; stage: string; otp: string }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.VERIFY_OTP,
        method: 'POST',
        body: body,
      }),
    }),

    policySubmission: builder.query<any, { proposal_id?: string }>({
      query: (data) => ({
        url: API_ENDPOINTS.BOB.POLICY_SUBMISSION,
        method: 'GET',
        params: data,
      }),
    }),

    // payment
    getPaymentLink: builder.query<any, { proposal_id?: string }>({
      query: (data) => ({
        url: API_ENDPOINTS.BOB.GET_PAYMENT_LINK,
        method: 'GET',
        params: data,
      }),
    }),
    getBOBPaymentLink: builder.query<any, { proposal_id?: string }>({
      query: (data) => ({
        url: API_ENDPOINTS.BOB.GET_BOB_PAYMENT_LINK,
        method: 'GET',
        params: data,
      }),
    }),
    autoDebitMode: builder.mutation<any, { proposal_id?: string; is_debit: boolean }>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.AUTO_DEBIT,
        method: 'POST',
        body: body,
      }),
    }),
    getPaymentStatus: builder.query<IPaymentResponse, { proposal_id?: string }>({
      query: (data) => ({
        url: API_ENDPOINTS.BOB.PAYMENT_STATUS,
        method: 'GET',
        params: data,
      }),
    }),
    getInitialCostomerConsentLink: builder.query<
      { link: string; consent_id: string },
      IInitialConsentLinkParams
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_INITIAL_CUSTOMER_CONSENT_LINK,
        method: 'GET',
        params: params,
      }),
    }),
    getInitialCostomerConsentStatus: builder.query<
      {
        status: 'APPROVED' | 'UNAPPROVED'
        comment: string
        retryable: boolean
      },
      { consent_id?: string }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_STATUS_INITIAL_CUSTOMER_CONSENT,
        method: 'GET',
        params: params,
      }),
    }),
    submitDeclarationDetails: builder.mutation<any, { name: string; mobile_number: string,relationship_with_LaPr:string,address:string,address1:string,proposal_id?:string,is_vernacdeclaration?:boolean,is_illiteratedeclaration?:boolean }>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.DECLARATION_DETAILS,
        method: 'POST',
        body: body,
      }),
    }),

    declarationSendOtp: builder.mutation<any, { type: string;proposal_id?:string}>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.DECLARATION_SEND_OTP,
        method: 'POST',
        body: body,
      }),
    }),

    declarationVerifyOtp: builder.mutation<any, {type: string;proposal_id?:string;otp:string}>({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.DECLARATION_VERIFY_OTP,
        method: 'POST',
        body: body,
      }),
    }),

    // person disability consent (IndiaFirst only)
    disabilityConsentSendLink: builder.mutation<
      IDisabilityConsentSendLinkResponse,
      IDisabilityConsentSendLinkRequest
    >({
      query: (body) => ({
        url: API_ENDPOINTS.BOB.DISABILITY_CONSENT_SEND_LINK,
        method: 'POST',
        body: body,
      }),
    }),

    getDisabilityConsentStatus: builder.query<
      IDisabilityConsentStatusResponse,
      { consent_id: string }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.DISABILITY_CONSENT_STATUS,
        method: 'GET',
        params: params,
      }),
    }),

  }),
})

export const {
  useGetChoicesQuery,
  useGetRecommendedPlansMutation,
  useGetQuotePlanQuery,
  useGetPlanOptionQuestionListQuery,
  useGetFatcaQuestionsQuery,
  useGetEcsQuestionsQuery,
  useGetEiaQuestionsQuery,
  useLazyGetEkycCustomerQuery,
  useGetSuitabilityMatrixQuestionsListQuery,
  useGetQuotationsMutation,
  useGetAssuredPersonalDetailsQuestionsQuery,
  useGetEducationalDetailsQuestionsQuery,
  useGetExistingPolicyDetailsQuestionsQuery,
  useSubmitExistingInsuranceDetalsMutation,
  useGetCreateProposalStatusMutation,
  useSubmitNomineeMutation,
  useGetSPDataQuery,
  useSubmitFatcaDetailsMutation,
  useSubmitEcsDetailsMutation,
  useSubmitEiaDetailsMutation,
  useGetDocListQuery,
  useSaveSutailblityMutation,

  // initial customer consent
  useLazyGetInitialCostomerConsentLinkQuery,
  useLazyGetInitialCostomerConsentStatusQuery,

  // Propposal Consent
  useLazyCheckProposalStatusQuery,
  useLazyGetConsentFormLinkQuery,

  useGetConsentFormLinkQuery,
  useSubmitHealthDetailsMutation,
  useSendProposalOtpMutation,
  useProposalSubmissionToInsurerMutation,
  useLazyGetSendConsentLinkQuery,
  useSubmitDeclarationDetailsMutation,
  useDeclarationSendOtpMutation,
  useDeclarationVerifyOtpMutation,

  // person disability consent
  useDisabilityConsentSendLinkMutation,
  useLazyGetDisabilityConsentStatusQuery,

  // ACR
  useSubmitAcrDetailsMutation,
  useLazyGetCheckSpStatusQuery,
  useGetCheckSpStatusQuery,
  useSendOtpSpConsentMutation,
  useVerifyOtpSpConsentMutation,
  useLazyPolicySubmissionQuery,

  // paymnet
  useLazyGetPaymentLinkQuery,
  useLazyGetBOBPaymentLinkQuery,
  useAutoDebitModeMutation,
  useLazyGetPaymentStatusQuery,
} = bobProductApi
